import classNames from "classnames";
import {formatInTimeZone} from "date-fns-tz";
import {forwardRef} from "react";

import {inclusivePeriodEnd, isSameDayInZone, timezoneAbbr} from "@/lib/format.tsx";
import {IncidentType, StatusPageTimeFormat, UptimeCalculationType} from "@/lib/types/statuspages.ts";

import {
  buildComponentStatusRank,
  getAffectedComponentsInPeriod,
  getDownComponentsInPeriod,
  getIncidentsInPeriod,
} from "../../utils/uptimeBarEvents.ts";
import {AffectedComponentsList} from "./AffectedComponentsList.tsx";
import {IncidentListSection} from "./IncidentListSection.tsx";
import type {StatusPageUptimeBarTooltipProps} from "./StatusPageUptimeBarTooltip.types.ts";

import styles from "./StatusPageUptimeBarTooltip.module.scss";

const MAX_LIST_LENGTH = 3;

const formatPeriodLabel = (
  periodStart: Date,
  periodEnd: Date,
  timezone: string,
  timeFormat?: StatusPageTimeFormat
): string => {
  const timeFmt = timeFormat === StatusPageTimeFormat.TWENTY_FOUR_HOUR ? "HH:mm:ss" : "hh:mm:ss a";
  const displayEnd = inclusivePeriodEnd(periodEnd);
  const startAbbr = timezoneAbbr(timezone, periodStart);
  const endAbbr = timezoneAbbr(timezone, displayEnd);
  // A period can straddle a DST change (e.g. EST\u2192EDT); label the start too when its abbreviation differs.
  const startAbbrSuffix = startAbbr !== endAbbr ? ` ${startAbbr}` : "";
  const startStr = `${formatInTimeZone(periodStart, timezone, `MMM d, yyyy, ${timeFmt}`)}${startAbbrSuffix}`;
  // Pills widen past 31 days; a 3-day pill labelled with one date reads as a single day.
  const endFmt = isSameDayInZone(periodStart, displayEnd, timezone) ? timeFmt : `MMM d, yyyy, ${timeFmt}`;
  const endStr = `${formatInTimeZone(displayEnd, timezone, endFmt)} ${endAbbr}`;
  return `${startStr} \u2013 ${endStr}`;
};

export const StatusPageUptimeBarTooltip = forwardRef<HTMLDivElement, StatusPageUptimeBarTooltipProps>(
  (
    {
      periodStart,
      periodEnd,
      incidents,
      statusMetadata,
      downtimeHistory,
      uptimeCalculationType,
      serviceComponents,
      timezone = "UTC",
      timeFormat,
      className,
      ...rest
    },
    ref
  ) => {
    const isFuturePeriod = periodStart.getTime() >= Date.now();

    if (uptimeCalculationType === UptimeCalculationType.BY_CHECKS) {
      const statusRank = buildComponentStatusRank(statusMetadata);
      const downComponents = getDownComponentsInPeriod(
        downtimeHistory,
        serviceComponents,
        statusRank,
        periodStart,
        periodEnd
      );
      return (
        <div ref={ref} className={classNames(styles.container, className)} {...rest}>
          <div className={styles.dateLabel}>{formatPeriodLabel(periodStart, periodEnd, timezone, timeFormat)}</div>
          {isFuturePeriod ? (
            <div className={styles.noDataLabel}>No Data</div>
          ) : (
            <AffectedComponentsList
              items={downComponents.map((c) => ({
                key: c.service_id,
                name: c.name,
                status: c.status,
                durationSecs: c.downtimeSecs,
              }))}
              maxListLength={MAX_LIST_LENGTH}
            />
          )}
        </div>
      );
    }

    const periodIncidents = getIncidentsInPeriod(incidents, periodStart, periodEnd);
    const statusRank = buildComponentStatusRank(statusMetadata);

    if (periodIncidents.length === 0) {
      return (
        <div ref={ref} className={classNames(styles.container, className)} {...rest}>
          <div className={styles.dateLabel}>{formatPeriodLabel(periodStart, periodEnd, timezone, timeFormat)}</div>
          {isFuturePeriod && <div className={styles.noDataLabel}>No Data</div>}
        </div>
      );
    }

    const sortBySeverity = (items: typeof periodIncidents) =>
      [...items].sort((a, b) => {
        const worstA = Math.min(...(a.affected_components?.map((c) => statusRank[c.status] ?? 99) ?? [99]));
        const worstB = Math.min(...(b.affected_components?.map((c) => statusRank[c.status] ?? 99) ?? [99]));
        if (worstA !== worstB) return worstA - worstB;
        return b.duration - a.duration;
      });

    const sortedIncidents = sortBySeverity(
      periodIncidents.filter((inc) => inc.incident_type === IncidentType.INCIDENT && inc.include_in_global_metrics)
    );
    const sortedMaintenance = sortBySeverity(
      periodIncidents.filter((inc) => inc.incident_type === IncidentType.SCHEDULED_MAINTENANCE)
    );

    const affectedComponents = getAffectedComponentsInPeriod(periodIncidents, statusRank);
    const componentEntries = Object.entries(affectedComponents).sort(
      ([, a], [, b]) => (statusRank[a] ?? 99) - (statusRank[b] ?? 99)
    );

    return (
      <div ref={ref} className={classNames(styles.container, className)} {...rest}>
        <div className={styles.dateLabel}>{formatPeriodLabel(periodStart, periodEnd, timezone, timeFormat)}</div>

        <IncidentListSection title="Incidents" items={sortedIncidents} maxListLength={MAX_LIST_LENGTH} />
        <IncidentListSection title="Maintenance" items={sortedMaintenance} maxListLength={MAX_LIST_LENGTH} />

        <AffectedComponentsList
          items={componentEntries.map(([name, status]) => ({key: name, name, status}))}
          maxListLength={MAX_LIST_LENGTH}
        />
      </div>
    );
  }
);

StatusPageUptimeBarTooltip.displayName = "StatusPageUptimeBarTooltip";
